// Practice Matches — domain rules.
//
// Practice matches run in the window between the Captain's Meeting publish
// (quals exist on the schedule) and BEGIN QUALS (Match Control switches to
// the real schedule). Referees create them ad-hoc from Match Control,
// pairing teams that have completed pre-inspection.
//
// Allowance — each team gets EITHER:
//   - 1 × 3v3 practice match (BO2), OR
//   - up to 2 × 1v1 practice matches (BO2 each)
// The two are mutually exclusive: a team that has played any 1v1 practice
// cannot book a 3v3 practice and vice versa. teams.practice_bonus grants N
// EXTRA bookings of the same format (default 0) — the create_practice_match
// RPC enforces it server-side; the client mirrors it here so bonus teams
// aren't filtered out of the picker before the RPC ever runs.

import type { PracticeMatch, QueueItem, Team, TeamId } from '../types';
import { isPreInspectionCompleteForEvent } from './preInspection';

export const PRACTICE_MAX_1V1 = 2;

export interface PracticeCounts {
  p3v3: number;
  p1v1: number;
}

/** How many practice matches a team is already booked into, per event.
 *  Every match counts toward the allowance regardless of completion —
 *  booking IS the allowance spend. */
export function practiceCountsFor(practiceMatches: readonly PracticeMatch[], team: TeamId): PracticeCounts {
  let p3v3 = 0;
  let p1v1 = 0;
  for (const m of practiceMatches) {
    if (m.red !== team && m.blue !== team) continue;
    if (m.event === '3v3') p3v3 += 1;
    else p1v1 += 1;
  }
  return { p3v3, p1v1 };
}

export interface PracticeEligibility {
  ok: boolean;
  /** Short human-readable reason when !ok — surfaced as a tooltip /
   *  filter explanation in the creation UI. */
  reason?: string;
}

export interface PracticeSlot {
  /** Wall-clock start time as the app's UTC-labelled ISO ('…THH:MM:00Z'). */
  iso: string;
  /** Display label, e.g. "2:20 PM". */
  label: string;
}

// Practice runs on one field per event in fixed slots: 3v3 = 20 min, 1v1 =
// 10 min. The next FREE slots from `nowMinutes` (skipping any already booked)
// — so NOW is the next free slot and a team can't reserve far ahead
// (first-come-first-serve). `nowMinutes`/`dateStr` come from the local clock at
// the call site (wall-clock minutes-of-day + YYYY-MM-DD); kept as params so the
// logic stays pure + testable.
export function practiceSlotMinutes(event: '3v3' | '1v1'): number {
  return event === '3v3' ? 20 : 10;
}

export function nextPracticeSlots(
  event: '3v3' | '1v1',
  practiceMatches: readonly PracticeMatch[],
  nowMinutes: number,
  dateStr: string,
  count = 4,
): PracticeSlot[] {
  const slotMin = practiceSlotMinutes(event);
  const slotOf = (iso: string): number => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return -1;
    return Math.floor((d.getUTCHours() * 60 + d.getUTCMinutes()) / slotMin) * slotMin;
  };
  const occupied = new Set(
    practiceMatches
      .filter((m) => m.event === event && m.scheduledAt)
      .map((m) => slotOf(m.scheduledAt as string)),
  );
  const pad = (n: number) => String(n).padStart(2, '0');
  const label = (hh: number, mm: number) => `${(hh % 12) || 12}:${pad(mm)} ${hh >= 12 ? 'PM' : 'AM'}`;
  const out: PracticeSlot[] = [];
  // Start at the next slot boundary AT/AFTER now (ceil) — never the block that's
  // already in progress (at 7:35 the next 20-min slot is 7:40, not 7:20).
  for (let s = Math.ceil(nowMinutes / slotMin) * slotMin; s < 24 * 60 && out.length < count; s += slotMin) {
    if (occupied.has(s)) continue;
    const hh = Math.floor(s / 60);
    const mm = s % 60;
    out.push({ iso: `${dateStr}T${pad(hh)}:${pad(mm)}:00Z`, label: label(hh, mm) });
  }
  return out;
}

/** Allowance check only (no pre-inspection): can `team` be booked into a
 *  new practice match for `event`? */
export function practiceAllowance(
  practiceMatches: readonly PracticeMatch[],
  team: TeamId,
  event: '3v3' | '1v1',
  /** Extra same-format bookings granted to this team (teams.practice_bonus).
   *  Added to the base cap; mirrors the create_practice_match RPC. */
  bonus = 0,
): PracticeEligibility {
  const counts = practiceCountsFor(practiceMatches, team);
  if (event === '3v3') {
    if (counts.p3v3 >= 1 + bonus) return { ok: false, reason: '3v3 practice already used' };
    if (counts.p1v1 >= 1) return { ok: false, reason: 'used 1v1 practice — cannot mix with 3v3' };
    return { ok: true };
  }
  if (counts.p3v3 >= 1) return { ok: false, reason: 'used 3v3 practice — cannot mix with 1v1' };
  if (counts.p1v1 >= PRACTICE_MAX_1V1 + bonus) return { ok: false, reason: `both 1v1 practices used` };
  return { ok: true };
}

/** Full eligibility for the creation picker: registered for the event,
 *  the event-relevant lineup pre-inspected, and allowance remaining.
 *  Readiness is event-scoped: 1v1 needs only the Infantry at l3 (a 3v3
 *  team with Hero/Sentry still pending can play 1v1 practice); 3v3 needs
 *  the full 3v3 lineup — the EC Engineer is never required. */
export function practiceEligibility(
  practiceMatches: readonly PracticeMatch[],
  queue: readonly QueueItem[],
  team: Team,
  event: '3v3' | '1v1',
  bonus = 0,
): PracticeEligibility {
  const eventKind = event === '3v3' ? '3v3' : '1v1';
  if (!team.events.includes(eventKind)) {
    return { ok: false, reason: `not registered for ${event}` };
  }
  if (!isPreInspectionCompleteForEvent(queue, team, event)) {
    return {
      ok: false,
      reason: event === '1v1' ? 'Infantry not pre-inspected' : 'needs 2+ robots pre-inspected for 3v3',
    };
  }
  return practiceAllowance(practiceMatches, team.id, event, bonus);
}
